<script lang="ts" setup>
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';

import { AppStars, AppSvgIcon, AppLinkButton } from '@libs/ui';

import type {
  AccommodationProductCardContent,
  AccommodationSegmentContext,
} from '../../../models';

interface AccommodationProductCardContentProps {
  productCardContent: AccommodationProductCardContent;
  context: AccommodationSegmentContext;
  shouldHideProductLink: boolean;
}

const props = defineProps<AccommodationProductCardContentProps>();

interface AccommodationProductCardEmits {
  (event: 'confirm-alternative'): void;
  (event: 'mouse-enter-content'): void;
  (event: 'mouse-leave-content'): void;
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardEmits>();
</script>

<template>
  <div
    class="accommodation-alternative-product-card-content"
    :class="{ 'should-hide-product-link': props.shouldHideProductLink }"
    @click="emit('show-details')"
    @mouseenter="emit('mouse-enter-content')"
    @mouseleave="emit('mouse-leave-content')"
  >
    <div class="details">
      <div class="upper-section">
        <div class="name">
          {{ props.productCardContent.header }}
        </div>
        <div v-if="props.productCardContent.subheader" class="location">
          {{ props.productCardContent.subheader }}
        </div>
        <AppStars :amount="props.productCardContent.rating.stars" size="18" />

        <HotelAmenities :amenities="props.productCardContent.amenities" />
      </div>

      <div
        class="middle-section"
        @click.stop
        @mouseenter="emit('mouse-leave-content')"
        @mouseleave="emit('mouse-enter-content')"
      >
        <div class="alternative-room">
          <div class="alternative-room-reason">
            <AppSvgIcon name="infoCircle" />
            {{ $t('planner.no_availability.messages.alternative_room') }}
          </div>

          <div class="altervative-rooms-info">
            <div
              v-for="element in props.productCardContent.cardDetails"
              :key="element.label + element.description"
              class="row"
            >
              <div class="label">{{ element.label }}:</div>
              <div class="description">{{ element.description }}</div>
            </div>
          </div>

          <div class="alternative-rooms-actions">
            <AppLinkButton
              :text="$t('planner.edit_room')"
              size="s"
              @click="emit('show-rooms')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.accommodation-alternative-product-card-content {
  height: 100%;

  .details {
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    padding: var(--spacing-16);
    flex: 1;

    @include for-less-or-equal-than-599-px {
      border-right: unset;
    }

    .upper-section {
      padding-bottom: var(--spacing-16);

      .name {
        padding: 0;
        @include font-label-l-bold;
        margin-bottom: var(--spacing-4);

        color: var(--color-neutral-70);
        text-decoration: none;

        @include on-hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }

      .location {
        @include font-label-s-light;
        margin-bottom: var(--spacing-4);
      }
    }

    .middle-section {
      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .label {
          @include font-label-s-bold;
          white-space: nowrap;
        }
        .description {
          @include font-label-s-light;
        }
      }

      .alternative-room {
        background: var(--color-brand-summer);
        padding: var(--spacing-16);
        border-radius: var(--border-radius-8);

        .alternative-room-reason {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-12);
          margin-bottom: var(--spacing-8);
          @include font-label-s-light;
        }

        .altervative-rooms-info {
          border-top: 1px solid var(--color-overlay);
          .row {
            display: flex;
            gap: var(--spacing-4);
            margin-top: var(--spacing-8);

            .label {
              @include font-label-s-bold;
              white-space: nowrap;
            }
            .description {
              @include font-label-s-light;
            }
          }
        }

        .alternative-rooms-actions {
          display: flex;
          gap: var(--spacing-8);
          margin-top: var(--spacing-16);
          @include font-label-s-light;
        }
      }
    }
  }

  .price {
    align-self: center;
  }
}
.should-hide-product-link {
  .details {
    .upper-section {
      .name {
        @include on-hover {
          cursor: auto;
          text-decoration: none;
        }
      }
    }
  }
}
</style>
